import SchemaUtils, {FieldWithName} from '../../lib/schema-utils';
import {expenseLineTotal} from "@/Case/Expense/expenseQuantity";
import util from '../../lib/util';
import * as DateUtil from '../../lib/dateutils';
import {formatFMLFromId, formatUserNameFromId} from '../../Contacts/ContactFormat';
import CaseHelper from '../../Case/CaseHelper';
import * as CampaignHelper from '../../CRM/Marketing/Campaigns/CampaignHelper';
import FinancialHelper from '../../Financial/FinancialHelper';
import LookupRegistry from '../LookupSelect/LookupRegistry';
import find from '../../store/find';
import {getEntityConfig} from './EntityMetaRegistry';
import type {KeyedObjectConfig} from './EntityMetaRegistry';
import type {EntitySchema} from '../../@types/schema';
import type Contact from '../../entities/contact';
import type Category from '../../entities/category';
import type Expense from '../../entities/expense';
import type FlatFee from '../../entities/flatFee';
import type FlatFeeMilestone from '../../entities/flatFeeMilestone';
import type Invoice from '../../entities/invoice';
import type Lead from '../../entities/lead';
import type Task from '../../entities/task';
import type Transaction from '../../entities/transaction';
import type ActivityType from '../../entities/activityType';

export type ChangeType = 'set' | 'cleared' | 'changed' | 'enabled' | 'disabled'
    | 'items_added' | 'items_removed';

export type FieldChange = {
    fieldName: string;
    displayName: string;
    changeType: ChangeType;
    prevDisplay: string;
    currDisplay: string;
    addedItems?: string[];
    removedItems?: string[];
};

/**
 * Resolve a UUID or ref object to a human-readable name using the schema's entityType/ref.
 * Returns null if no resolution is possible (caller should fall back to other formatting).
 */
function resolveEntityRef(value: any, field: FieldWithName): string | null {
    const entityType = field.ref || field.entityType;
    if (!entityType) return null;

    const isRef = !!field.ref;

    switch (entityType) {
        case 'activityType': {
            const at = find<ActivityType>('activityTypes', value);
            return at?.name || null;
        }
        case 'caseType':
        case 'caseTypeId':
            return CaseHelper.formatCaseTypeFromId(value) || null;
        case 'client':
        case 'contact': {
            if (isRef && typeof value === 'object') {
                const name = [value.first, value.last].filter(Boolean).join(' ');
                return name || null;
            }
            return formatFMLFromId(value) || null;
        }
        case 'campaign':
            return CampaignHelper.getCampaignFromId(value)?.name || null;
        case 'category': {
            const cat = isRef ? value : find<Category>('categories', value);
            return cat?.name || null;
        }
        case 'expense': {
            const exp = find<Expense>('expenses', value);
            if (!exp) return null;
            const payee = formatFMLFromId(exp.payee);
            const desc = exp.description || '';
            const amount = util.formatCurrencyAsString(expenseLineTotal(exp.quantity, exp.cost));
            return `${amount} to ${payee}: ${desc}`.trim();
        }
        case 'flatFee': {
            if (isRef && typeof value === 'object') {
                const amount = util.formatCurrencyAsString(value.amount);
                return `${value.name}: ${amount}`;
            }
            const ff = find<FlatFee>('flatFees', value);
            return ff?.name || null;
        }
        case 'flatFeeMilestone': {
            const ffm = find<FlatFeeMilestone>('flatFeeMilestones', value);
            if (!ffm) return null;
            return `${ffm.name}: ${util.formatCurrencyAsString(ffm.amount)}`;
        }
        case 'invoice': {
            const inv = isRef ? value : FinancialHelper.getInvoiceById(value);
            return inv ? `Invoice #${inv.invoiceNumber}` : null;
        }
        case 'lead': {
            const lead = find<Lead>('leads', value);
            return lead?.name || null;
        }
        case 'matter':
            return CaseHelper.getMatterFromId(value)?.name || null;
        case 'milestone': {
            if (typeof value === 'object' && value.name) {
                const parts = [value.name];
                if (value.dateCompleted) parts.push(`completed ${DateUtil.formatDate(value.dateCompleted)}`);
                return parts.join(', ');
            }
            return null;
        }
        case 'participant': {
            if (typeof value === 'object' && value.contactId) {
                return formatFMLFromId(value.contactId) || null;
            }
            return null;
        }
        case 'referral': {
            if (typeof value === 'object') {
                const referrer = formatFMLFromId(value.referrer);
                const lead = find<Lead>('leads', value.referral)?.name || '';
                return `${referrer} referred ${lead}`.trim();
            }
            return null;
        }
        case 'task': {
            const task = find<Task>('tasks', value);
            return task?.title || null;
        }
        case 'transaction': {
            const tx = find<Transaction>('transactions', value);
            if (!tx) return null;
            return `${util.formatCurrencyAsString(tx.amount)} (${DateUtil.formatDate(tx.txDate)})`;
        }
        case 'user':
            if (isRef && typeof value === 'object') {
                return [value.first, value.last].filter(Boolean).join(' ') || null;
            }
            return formatUserNameFromId(value) || null;
        case 'event': {
            if (typeof value === 'object') {
                const cat = LookupRegistry.getLookupById(value.category)?.key || '';
                const date = value.occurredOn ? DateUtil.formatDate(value.occurredOn) : '';
                return `${cat} on ${date}`.trim();
            }
            return null;
        }
        case 'resource': {
            if (typeof value === 'object') {
                return `${value.type}: ${value.value}`;
            }
            return null;
        }
        case 'redFlag': {
            if (typeof value === 'object') {
                return value.notes || 'Red Flag';
            }
            return null;
        }
        case 'identification': {
            if (typeof value === 'object') {
                return `${value.method} ${value.identifier}`.trim();
            }
            return null;
        }
        case 'comment': {
            if (typeof value === 'object' && value.note) {
                const plain = value.note.replace(/<[^>]+>/g, '').substring(0, 80);
                return plain || null;
            }
            return null;
        }
        default:
            return null;
    }
}

/**
 * Format a single value to a display string using textTransforms, entity resolution, or schema-aware defaults.
 */
function formatValueToText(
    value: any,
    field: FieldWithName,
    textTransforms: Record<string, (v: any) => string>
): string {
    if (value === null || value === undefined) return '';

    // Use text transform if available
    const transform = textTransforms[field.name];
    if (transform) {
        const result = transform(value);
        return result != null ? String(result) : '';
    }

    // Try entity reference resolution
    const resolved = resolveEntityRef(value, field);
    if (resolved) return resolved;

    // Schema-aware formatting
    if (field.type === 'date') return DateUtil.formatDate(value, false, false, false) || '';
    if (field.type === 'timestamp') return DateUtil.formatDateTime(value) || '';
    if (field.type === 'boolean') return value === true ? 'Yes' : 'No';

    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

/**
 * Format an array item to a display string.
 */
function formatArrayItemToText(
    item: any,
    field: FieldWithName,
    textTransforms: Record<string, (v: any) => string>
): string {
    const transform = textTransforms[field.name];
    if (transform) {
        const result = transform(item);
        return result != null ? String(result) : '';
    }

    // Try entity reference resolution
    const resolved = resolveEntityRef(item, field);
    if (resolved) return resolved;

    if (typeof item === 'object') return JSON.stringify(item);
    return String(item);
}

/**
 * Deep includes check (for ref/json arrays).
 */
function deepIncludes(arr: any[], item: any): boolean {
    if (!Array.isArray(arr)) return false;
    return arr.some(o => util.deepCompare(o, item));
}

/**
 * Classify what type of change occurred between two values.
 */
function classifyChange(field: FieldWithName, prev: any, curr: any): ChangeType {
    if (field.type === 'boolean') {
        if (curr === true && !prev) return 'enabled';
        if (!curr && prev === true) return 'disabled';
    }

    if (prev == null && curr != null) return 'set';
    if (prev != null && curr == null) return 'cleared';
    return 'changed';
}

/**
 * Compute the list of field changes between two versions of an entity.
 */
export function computeChanges(entityName: string, prev: any, curr: any, entity?: any): FieldChange[] {
    const config = getEntityConfig(entityName, entity || curr);
    if (!config) return [];

    const {entityClass, ignoreFields, nameMap, textTransforms, keyedObjectFields} = config;
    const schema: EntitySchema = entityClass.getSchema();
    const fields = SchemaUtils.getFields(schema);
    const entityIdField = schema.entity + 'Id';

    const allIgnored = [...ignoreFields, entityIdField];

    return fields
        .flatMap(field => processField(field, prev, curr, allIgnored, nameMap, textTransforms, schema, keyedObjectFields))
        .filter((c): c is FieldChange => c !== null);
}

function processField(
    field: FieldWithName,
    prev: any,
    curr: any,
    ignoreFields: string[],
    nameMap: Record<string, string>,
    textTransforms: Record<string, (v: any) => string>,
    schema: EntitySchema,
    keyedObjectFields?: Record<string, KeyedObjectConfig>
): (FieldChange | null)[] {
    const name = field.name;
    if (ignoreFields.includes(name)) return [];

    let prevField = prev ? prev[name] : null;
    let currField = curr ? curr[name] : null;

    // Handle numeric types: 0 equals null
    if (['float', 'int', 'integer', 'bigint'].includes(field.type)) {
        if ((prevField || 0) === (currField || 0)) return [];
    }
    // Handle boolean types: null equals false
    else if (field.type === 'boolean') {
        if ((prevField ?? false) === (currField ?? false)) return [];
    }
    // Handle date/time types
    else if (['date', 'datetime', 'timestamp'].includes(field.type)) {
        if (typeof prevField === 'string') prevField = new Date(prevField);
        if (typeof currField === 'string') currField = new Date(currField);
    }
    // Handle json(b) types
    else if (field.type.startsWith('json')) {
        if (field.type.endsWith('[]')) {
            if (prevField?.length === 0 && currField?.length === 0) return [];
        } else {
            if (prevField === '{}') prevField = {};
            if (currField === '{}') currField = {};
            if (Object.entries(prevField ?? {}).length === 0 && Object.entries(currField ?? {}).length === 0) return [];
        }
    }

    const isArray = field.type.endsWith('[]');
    const isRef = !!field.ref;
    const isEntityArray = (field.entityType || field.entity) && isArray;

    const displayName = nameMap[name] || util.camelCaseToWords(name);

    // Handle arrays (entity/ref/json/simple)
    if (isEntityArray || (isRef && isArray)) {
        prevField = prevField || [];
        currField = currField || [];

        const prevStrings = prevField.map((v: any) => JSON.stringify(v));
        const currStrings = currField.map((v: any) => JSON.stringify(v));

        const same = prevField.length === currField.length &&
            util.arrayIntersection(prevStrings, currStrings).length === prevField.length;

        if (same) return [];

        return [buildArrayChange(field, prevField, currField, displayName, textTransforms)];
    }
    else if (isArray) {
        prevField = prevField || [];
        currField = currField || [];

        if (prevField.length === 0 && currField.length === 0) return [];

        const prevSet = new Set(prevField.map(String));
        const currSet = new Set(currField.map(String));

        if (prevField.length === currField.length && prevField.every((v: any) => currSet.has(String(v)))) return [];

        return [buildArrayChange(field, prevField, currField, displayName, textTransforms)];
    }

    // Handle keyed objects (diff individual keys instead of showing the whole object)
    const keyedConfig = keyedObjectFields?.[name];
    if (keyedConfig) {
        if (util.deepCompare(prevField, currField)) return [];
        return processKeyedObject(name, displayName, prevField, currField, keyedConfig);
    }

    // Handle objects
    if ((field.entityType || (prevField !== null && typeof prevField === 'object') || (currField !== null && typeof currField === 'object'))) {
        if (util.deepCompare(prevField, currField)) return [];

        const changeType = classifyChange(field, prevField, currField);
        return [{
            fieldName: name,
            displayName,
            changeType,
            prevDisplay: formatValueToText(prevField, field, textTransforms),
            currDisplay: formatValueToText(currField, field, textTransforms),
        }];
    }

    // Handle scalars
    if (prevField === currField) return [];

    const changeType = classifyChange(field, prevField, currField);
    return [{
        fieldName: name,
        displayName,
        changeType,
        prevDisplay: formatValueToText(prevField, field, textTransforms),
        currDisplay: formatValueToText(currField, field, textTransforms),
    }];
}

/**
 * Diff a keyed object field by comparing individual keys after optional normalization.
 * Produces a separate FieldChange for each key that changed.
 * Only keys listed in config.nameMap are tracked; all others are ignored.
 */
function processKeyedObject(
    fieldName: string,
    parentDisplayName: string,
    prev: any,
    curr: any,
    config: KeyedObjectConfig
): FieldChange[] {
    const normalize = config.normalize || ((v: any) => v);
    const prevNorm = normalize(prev) || {};
    const currNorm = normalize(curr) || {};

    const changes: FieldChange[] = [];

    for (const [key, keyDisplayName] of Object.entries(config.nameMap)) {
        const prevVal = prevNorm[key] ?? null;
        const currVal = currNorm[key] ?? null;

        if (util.deepCompare(prevVal, currVal)) continue;

        const textTransform = config.textTransforms?.[key];
        const prevDisplay = textTransform ? textTransform(prevVal) : (prevVal != null ? String(prevVal) : '');
        const currDisplay = textTransform ? textTransform(currVal) : (currVal != null ? String(currVal) : '');

        // Skip if both display as empty (e.g., amount_refunded transform returns '' for 0)
        if (!prevDisplay && !currDisplay) continue;

        let changeType: ChangeType;
        if (prevVal == null && currVal != null) changeType = 'set';
        else if (prevVal != null && currVal == null) changeType = 'cleared';
        else changeType = 'changed';

        changes.push({
            fieldName: `${fieldName}.${key}`,
            displayName: `${parentDisplayName} ${keyDisplayName}`,
            changeType,
            prevDisplay,
            currDisplay,
        });
    }

    return changes;
}

/**
 * Build a FieldChange for array fields, computing added/removed items.
 */
function buildArrayChange(
    field: FieldWithName,
    prevArr: any[],
    currArr: any[],
    displayName: string,
    textTransforms: Record<string, (v: any) => string>
): FieldChange | null {
    const isJsonOrRef = !!field.ref || field.type.startsWith('json');

    let added: any[];
    let removed: any[];

    if (isJsonOrRef) {
        added = currArr.filter(item => !deepIncludes(prevArr, item));
        removed = prevArr.filter(item => !deepIncludes(currArr, item));
    } else {
        const prevSet = new Set(prevArr.map(String));
        const currSet = new Set(currArr.map(String));
        added = currArr.filter(item => !prevSet.has(String(item)));
        removed = prevArr.filter(item => !currSet.has(String(item)));
    }

    const addedItems = added.map(item => formatArrayItemToText(item, field, textTransforms));
    const removedItems = removed.map(item => formatArrayItemToText(item, field, textTransforms));

    const changes: FieldChange[] = [];

    if (removed.length > 0) {
        changes.push({
            fieldName: field.name,
            displayName,
            changeType: 'items_removed',
            prevDisplay: '',
            currDisplay: '',
            removedItems,
        });
    }

    if (added.length > 0) {
        changes.push({
            fieldName: field.name,
            displayName,
            changeType: 'items_added',
            prevDisplay: '',
            currDisplay: '',
            addedItems,
        });
    }

    // If both empty but arrays differ (edge case), return a generic change
    if (changes.length === 0) {
        return {
            fieldName: field.name,
            displayName,
            changeType: 'changed',
            prevDisplay: `${prevArr.length} items`,
            currDisplay: `${currArr.length} items`,
        };
    }

    // Return the first change; if both added and removed, combine
    if (changes.length === 1) return changes[0];

    return {
        fieldName: field.name,
        displayName,
        changeType: 'changed',
        prevDisplay: '',
        currDisplay: '',
        addedItems,
        removedItems,
    };
}
